/* eslint-disable no-magic-numbers */
import { useState } from 'react';
import { SerializedError } from '@reduxjs/toolkit';
import { useAppSelector } from '@/redux/hooks/useAppSelector';
import { useAppDispatch } from '@/redux/hooks/useAppDispatch';
import {
  addElementAnnotation,
  deleteElementAnnotation,
  getModelElementsForModel,
  updateElementMeta,
  updateElementAnnotation,
} from '@/redux/modelElements/modelElements.thunks';
import { showToast } from '@/utils/showToast';
import { elementMetaStateSelector } from '@/redux/modal/modal.selector';
import { modelElementsByModelIdSelector } from '@/redux/modelElements/modelElements.selector';
import { reactionsByModelIdSelector } from '@/redux/reactions/reactions.selector';
import { COMPARTMENT_SBO_TERM } from '@/components/Map/MapViewer/MapViewer.constants';
import { FontName } from '@/components/Map/MapViewer/MapViewer.types';
import { ModelElement, Annotation } from '@/types/models';
import { UpdateModelElementData } from '@/redux/modelElements/modelElements.types';
import {
  MAX_LINE_WIDTH,
  MIN_LINE_WIDTH,
} from '@/components/FunctionalArea/Modal/LayerLineFactoryModal/LayerLineFactory.constants';

const DEFAULT_FONT_SIZE = 0;
const DEFAULT_Z = 0;

let annotationIdCounter = -1;

const createEmptyAnnotation = (): Annotation => {
  const id = annotationIdCounter;
  annotationIdCounter -= 1;
  return { id, link: null, article: null, type: '', resource: '', annotator: null };
};

/**
 * Owns all editable element-property state (identity, styling, stacking,
 * annotations) plus the auto-save flow shared by every control in
 * {@link ElementPropertiesForm}. Each control updates its local state and calls
 * `save({ field })` with the new value so the write never races the (async)
 * state update. Presentational markup lives in the form component.
 */
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export const useElementProperties = () => {
  const dispatch = useAppDispatch();

  const elementMetaState = useAppSelector(elementMetaStateSelector);
  const modelElements = useAppSelector(state =>
    modelElementsByModelIdSelector(state, elementMetaState?.model || 0),
  );
  const reactions = useAppSelector(state =>
    reactionsByModelIdSelector(state, elementMetaState?.model || 0),
  );
  const modelElement = modelElements.find(element => element.id === elementMetaState?.id);

  const isCompartment = modelElement?.sboTerm === COMPARTMENT_SBO_TERM;

  const [isSending, setIsSending] = useState<boolean>(false);

  const [name, setName] = useState<string>(modelElement?.name || '');
  const [notes, setNotes] = useState<string>(modelElement?.notes || '');
  const [immediateLink, setImmediateLink] = useState<string>(modelElement?.immediateLink || '');
  const [fontSize, setFontSize] = useState<number>(modelElement?.fontSize ?? DEFAULT_FONT_SIZE);
  const [fontName, setFontName] = useState<FontName | undefined>(modelElement?.fontName);
  const [fontColor, setFontColor] = useState(modelElement?.fontColor);
  const [fillColor, setFillColor] = useState(modelElement?.fillColor);
  const [borderColor, setBorderColor] = useState(modelElement?.borderColor);
  const [nameHorizontalAlign, setNameHorizontalAlign] = useState(modelElement?.nameHorizontalAlign);
  const [nameVerticalAlign, setNameVerticalAlign] = useState(modelElement?.nameVerticalAlign);
  const [lineWidth, setLineWidth] = useState<number>(modelElement?.lineWidth ?? MIN_LINE_WIDTH);
  const [innerWidth, setInnerWidth] = useState<number>(modelElement?.innerWidth ?? MIN_LINE_WIDTH);
  const [outerWidth, setOuterWidth] = useState<number>(modelElement?.outerWidth ?? MIN_LINE_WIDTH);
  const [annotations, setAnnotations] = useState<Annotation[]>(modelElement?.references || []);
  const [z, setZ] = useState<number>(modelElement?.z ?? DEFAULT_Z);
  const [allowUiInteraction, setAllowUiInteraction] = useState<boolean>(
    modelElement?.allowUiInteraction ?? true,
  );

  const save = async (overrides: Partial<UpdateModelElementData> = {}): Promise<void> => {
    if (!elementMetaState || !modelElement) {
      return;
    }
    setIsSending(true);
    try {
      await dispatch(
        updateElementMeta({
          element: modelElement,
          data: {
            immediateLink,
            name,
            notes,
            fontSize,
            fontName,
            fontColor,
            fillColor,
            borderColor,
            nameHorizontalAlign,
            nameVerticalAlign,
            z,
            allowUiInteraction,
            ...(isCompartment ? { innerWidth, outerWidth } : { lineWidth }),
            ...overrides,
          },
        }),
      ).unwrap();
      dispatch(getModelElementsForModel(elementMetaState.model));
    } catch (error) {
      const typedError = error as SerializedError;
      showToast({
        type: 'error',
        message: typedError.message || 'An error occurred while updating the element.',
      });
    } finally {
      setIsSending(false);
    }
  };

  const clampLineWidth = (value: number): number =>
    Math.min(MAX_LINE_WIDTH, Math.max(MIN_LINE_WIDTH, value));

  // Annotations have dedicated create/update/delete endpoints,
  // separate from the element PUT above. A row with a negative id is a local
  // draft not yet persisted; a positive id is a saved annotation. After any
  // server write we re-read the element so drafts pick up their real id/link.
  const modelId = elementMetaState?.model;
  const elementId = modelElement?.id;

  const syncAnnotationsFromServer = async (): Promise<void> => {
    if (modelId === undefined || elementId === undefined) {
      return;
    }
    const elements = await dispatch(getModelElementsForModel(modelId)).unwrap();
    const updated = elements?.find(element => element.id === elementId);
    if (updated) {
      setAnnotations(updated.references);
    }
  };

  // Creates a draft, updates an existing row; incomplete rows are left untouched.
  const persistAnnotation = async (annotation: Annotation): Promise<void> => {
    if (modelId === undefined || elementId === undefined) {
      return;
    }
    const annotationData = { type: annotation.type.trim(), resource: annotation.resource.trim() };
    if (!annotationData.type || !annotationData.resource) {
      return;
    }
    setIsSending(true);
    try {
      if (annotation.id < 0) {
        await dispatch(addElementAnnotation({ modelId, elementId, annotationData })).unwrap();
      } else {
        await dispatch(
          updateElementAnnotation({
            modelId,
            elementId,
            annotationId: annotation.id,
            annotationData,
          }),
        ).unwrap();
      }
      await syncAnnotationsFromServer();
    } catch (error) {
      const typedError = error as SerializedError;
      showToast({
        type: 'error',
        message: typedError.message || 'An error occurred while saving the annotation.',
      });
    } finally {
      setIsSending(false);
    }
  };

  const handleAnnotationChange = (
    index: number,
    key: keyof Pick<Annotation, 'type' | 'resource'>,
    value: string,
  ): void => {
    setAnnotations(prev =>
      prev.map((annotation, i) => (i === index ? { ...annotation, [key]: value } : annotation)),
    );
  };

  const handleAddAnnotation = (): void => {
    setAnnotations(prev => [...prev, createEmptyAnnotation()]);
  };

  // The resource is a free-text field, so it persists on blur.
  const handleAnnotationBlur = (index: number): void => {
    const annotation = annotations[index];
    if (annotation) {
      persistAnnotation(annotation);
    }
  };

  // The type is a dropdown (no blur), so persist right after selection.
  const handleAnnotationTypeChange = (index: number, type: string): void => {
    const updated = annotations.map((annotation, i) =>
      i === index ? { ...annotation, type } : annotation,
    );
    setAnnotations(updated);
    persistAnnotation(updated[index]);
  };

  const handleRemoveAnnotation = async (index: number): Promise<void> => {
    const annotation = annotations[index];
    if (!annotation) {
      return;
    }
    // A draft that was never persisted is just dropped locally.
    if (annotation.id < 0) {
      setAnnotations(prev => prev.filter((_annotation, i) => i !== index));
      return;
    }
    if (modelId === undefined || elementId === undefined) {
      return;
    }
    setIsSending(true);
    try {
      await dispatch(
        deleteElementAnnotation({ modelId, elementId, annotationId: annotation.id }),
      ).unwrap();
      await syncAnnotationsFromServer();
    } catch (error) {
      const typedError = error as SerializedError;
      showToast({
        type: 'error',
        message: typedError.message || 'An error occurred while deleting the annotation.',
      });
    } finally {
      setIsSending(false);
    }
  };

  // Stacking is computed across every element and reaction on the same diagram.
  const handleBringToFront = (): void => {
    const maxZ = [...modelElements, ...reactions].reduce(
      (max, element) => Math.max(max, element.z),
      z,
    );
    const newZ = maxZ + 1;
    setZ(newZ);
    save({ z: newZ });
  };

  const handleSendToBack = (): void => {
    const minZ = [...modelElements, ...reactions].reduce(
      (min, element) => Math.min(min, element.z),
      z,
    );
    const newZ = minZ - 1;
    setZ(newZ);
    save({ z: newZ });
  };

  const handleToggleUiInteraction = (checked: boolean): void => {
    setAllowUiInteraction(checked);
    save({ allowUiInteraction: checked });
  };

  return {
    isCompartment,
    isSending,
    save,
    clampLineWidth,
    name,
    setName,
    notes,
    setNotes,
    immediateLink,
    setImmediateLink,
    fontSize,
    setFontSize,
    fontName,
    setFontName,
    fontColor,
    setFontColor,
    fillColor,
    setFillColor,
    borderColor,
    setBorderColor,
    nameHorizontalAlign,
    setNameHorizontalAlign,
    nameVerticalAlign,
    setNameVerticalAlign,
    lineWidth,
    setLineWidth,
    innerWidth,
    setInnerWidth,
    outerWidth,
    setOuterWidth,
    annotations,
    z,
    allowUiInteraction,
    handleToggleUiInteraction,
    handleAnnotationChange,
    handleAnnotationBlur,
    handleAnnotationTypeChange,
    handleAddAnnotation,
    handleRemoveAnnotation,
    handleBringToFront,
    handleSendToBack,
  };
};

export type ElementAlignHorizontal = ModelElement['nameHorizontalAlign'];
export type ElementAlignVertical = ModelElement['nameVerticalAlign'];
